import { User, Ruler, Weight, Target, Calendar, MapPin, Dumbbell } from "lucide-react"
import { cn } from "@/lib/utils"

interface UserProfileCardProps {
  profile: Record<string, unknown>
  className?: string
  compact?: boolean
}

interface LiftMaxes {
  squat?: number
  bench?: number
  deadlift?: number
}

function extractMaxes(profile: Record<string, unknown>): LiftMaxes | null {
  const onboarding = profile.onboarding_data as Record<string, unknown> | undefined
  if (!onboarding) return null
  const wl = onboarding.weightlifting as Record<string, unknown> | undefined
  if (!wl) return null
  const maxes = wl.maxes as LiftMaxes | undefined
  if (!maxes) return null
  const hasAny =
    (typeof maxes.squat === "number" && maxes.squat > 0) ||
    (typeof maxes.bench === "number" && maxes.bench > 0) ||
    (typeof maxes.deadlift === "number" && maxes.deadlift > 0)
  return hasAny ? maxes : null
}

function formatLift(value?: number): string {
  return typeof value === "number" && value > 0 ? `${value} lbs` : "—"
}

function formatHeight(feet?: number, inches?: number): string | null {
  if (feet == null) return null
  return inches ? `${feet}'${inches}"` : `${feet}'`
}

// Map goal-type slug → human label. Mobile onboarding has stored multiple
// historical slugs (lose-fat vs lose-body-fat, etc.) so we accept both.
// Anything unrecognized falls through as the raw slug rather than dropping —
// otherwise a future slug would silently render an empty "Goals: " row.
const GOAL_TYPE_LABEL: Record<string, string> = {
  "gain-muscle": "Build muscle",
  "build-muscle": "Build muscle",
  "lose-fat": "Lose fat",
  "lose-body-fat": "Lose fat",
  "get-stronger": "Get stronger",
  "get-faster": "Get faster",
  "train-event": "Train for an event",
  "push-myself": "Push myself",
}

function labelForGoalType(type: string): string {
  return GOAL_TYPE_LABEL[type] ?? type
}

interface GoalShape {
  name?: string
  type?: string
  muscleGroups?: string[]
  eventType?: string
  targetBodyFat?: number
  currentBodyFat?: number
  details?: string
}

function formatGoals(goals?: unknown): string | null {
  if (!goals || !Array.isArray(goals) || goals.length === 0) return null
  const out: string[] = []
  for (const raw of goals) {
    if (typeof raw === "string") {
      out.push(raw)
      continue
    }
    const g = raw as GoalShape
    // Prefer the stored human name; fall back to mapping the type slug.
    const head = g.name ?? (g.type ? labelForGoalType(g.type) : null)
    if (!head) continue
    // Surface the most useful sub-detail inline so the admin doesn't have
    // to drill into the raw profile JSON for "what muscle groups" / "what event".
    let detail: string | null = null
    if (g.muscleGroups && g.muscleGroups.length > 0) {
      detail = g.muscleGroups.join("/")
    } else if (g.eventType) {
      detail = g.eventType
    } else if (typeof g.targetBodyFat === "number") {
      detail = `${g.currentBodyFat ?? "?"}%→${g.targetBodyFat}%`
    }
    out.push(detail ? `${head} (${detail})` : head)
  }
  return out.length > 0 ? out.join(", ") : null
}

function formatSchedule(schedule?: Record<string, number>): string | null {
  if (!schedule || Object.keys(schedule).length === 0) return null
  return Object.entries(schedule)
    .map(([day, count]) => `${day}: ${count}`)
    .join(", ")
}

// Map mobile-onboarding short codes ("M", "Tu", ...) to full day names so
// the admin sees "Monday, Tuesday, Wednesday" instead of "M, Tu, W". Some
// users (notably ones who went through the Playground or later versions
// of onboarding) already store full names — those pass through unchanged.
const SHORT_DAY_MAP: Record<string, string> = {
  M: "Monday",
  Tu: "Tuesday",
  W: "Wednesday",
  Th: "Thursday",
  F: "Friday",
  Sa: "Saturday",
  Su: "Sunday",
}

function expandDay(d: string): string {
  return SHORT_DAY_MAP[d] ?? d
}

function formatPreferredDays(days?: Record<string, string[]>): string | null {
  if (!days || Object.keys(days).length === 0) return null
  // Filter out activities the user picked but left blank ("Other: []") so
  // they don't pollute the display.
  const populated = Object.entries(days).filter(
    ([, dayList]) => Array.isArray(dayList) && dayList.length > 0,
  )
  if (populated.length === 0) return null
  return populated
    .map(([activity, dayList]) => `${activity}: ${dayList.map(expandDay).join(", ")}`)
    .join(" | ")
}

function Field({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number | null | undefined
}) {
  if (value == null || value === "") return null
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <div>
        <span className="text-muted-foreground">{label}: </span>
        <span className="font-medium">{value}</span>
      </div>
    </div>
  )
}

export function UserProfileCard({
  profile,
  className,
  compact = false,
}: UserProfileCardProps) {
  const p = profile as Record<string, unknown>

  const name = [p.first_name, p.last_name].filter(Boolean).join(" ")
  const age = p.age as number | undefined
  const gender = p.gender as string | undefined
  const location = p.location as string | undefined
  const heightStr = formatHeight(
    p.height_feet as number | undefined,
    p.height_inches as number | undefined,
  )
  const weight = p.weight_lbs as number | undefined
  const goals = formatGoals(p.goals as unknown)
  const activities = Array.isArray(p.selected_activities)
    ? (p.selected_activities as string[]).join(", ")
    : null
  const schedule = formatSchedule(
    p.training_schedule as Record<string, number> | undefined,
  )
  const preferredDays = formatPreferredDays(
    p.preferred_days as Record<string, string[]> | undefined,
  )
  const summary = p.profile_summary as string | undefined
  const maxes = extractMaxes(p)

  if (compact) {
    return (
      <div className={cn("flex flex-wrap gap-x-4 gap-y-1 text-sm", className)}>
        {name && <span className="font-semibold">{name}</span>}
        {age && <span className="text-muted-foreground">{age}y</span>}
        {heightStr && (
          <span className="text-muted-foreground">{heightStr}</span>
        )}
        {weight && (
          <span className="text-muted-foreground">{weight} lbs</span>
        )}
        {goals && <span className="text-muted-foreground">{goals}</span>}
      </div>
    )
  }

  return (
    <div className={cn("rounded-lg border bg-card p-4 space-y-2", className)}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{name || "Unknown User"}</h3>
        {age && (
          <span className="text-sm text-muted-foreground">
            {age}y{gender ? ` / ${gender}` : ""}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        <Field icon={Ruler} label="Height" value={heightStr} />
        <Field icon={Weight} label="Weight" value={weight ? `${weight} lbs` : null} />
        <Field icon={MapPin} label="Location" value={location} />
        <Field icon={Target} label="Goals" value={goals} />
        <Field icon={User} label="Activities" value={activities} />
        <Field icon={Calendar} label="Schedule" value={schedule} />
      </div>

      {preferredDays && (
        <div className="text-sm">
          <span className="text-muted-foreground">Preferred days: </span>
          <span>{preferredDays}</span>
        </div>
      )}

      {maxes && (
        <div className="border-t pt-2 mt-2 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Dumbbell className="size-3.5 text-muted-foreground" />
            <span>One-rep max</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Squat: </span>
              <span className="font-medium">{formatLift(maxes.squat)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Bench: </span>
              <span className="font-medium">{formatLift(maxes.bench)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Deadlift: </span>
              <span className="font-medium">{formatLift(maxes.deadlift)}</span>
            </div>
          </div>
        </div>
      )}

      {summary && (
        <p className="text-sm text-muted-foreground italic border-t pt-2 mt-2">
          {summary}
        </p>
      )}
    </div>
  )
}
